import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, Calendar, Printer, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient, { getAuthToken } from '@/utils/api';
import { jwtDecode } from 'jwt-decode';

const PastOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      let perms = decoded.permissions || ['orders'];
      if (decoded.user_type === 'owner') {
        setUserPermissions(['all']);
      } else {
        const expandedPerms = new Set(perms);
        if (perms.includes('orders')) {
          expandedPerms.add('view_orders').add('checkout_orders').add('print_receipt').add('view_past_orders').add('print_past_receipt');
        }
        setUserPermissions(Array.from(expandedPerms));
      }
    } catch (e) {
      console.error(e);
    }

    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [navigate]);

  const hasPermission = (p) => userPermissions.includes('all') || userPermissions.includes(p);

  const fetchOrders = async () => {
    try {
      const response = await apiClient.get('/orders');
      // SADECE geçmiş (Tamamlanmış) siparişleri göster
      const pastOrders = response.data.filter(order => order.status === 'completed');
      
      const groupedMap = new Map();
      pastOrders.forEach(order => {
        const key = `${order.table_number}_${order.updated_at}`;
        if (!groupedMap.has(key)) {
          groupedMap.set(key, {
            id: order.id,
            table_number: order.table_number,
            updated_at: order.updated_at,
            created_at: order.created_at,
            items: [],
            total_amount: 0,
            checkout_payments: order.checkout_payments || []
          });
        }
        
        const group = groupedMap.get(key);
        
        order.items.forEach(newItem => {
            const existingItem = group.items.find(i => i.menu_item_name === newItem.menu_item_name && i.price === newItem.price);
            if (existingItem) {
                existingItem.quantity += newItem.quantity;
            } else {
                group.items.push({...newItem});
            }
        });
        
        group.total_amount += order.total_amount;
        
        if (order.checkout_payments && order.checkout_payments.length > 0 && group.checkout_payments.length === 0) {
            group.checkout_payments = order.checkout_payments;
        }
      });
      
      const mergedPastOrders = Array.from(groupedMap.values()).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setOrders(mergedPastOrders);
    } catch (error) {
      console.error('Geçmiş siparişler yüklenirken hata:', error);
    }
  };

  const printReceipt = async (orderId) => {
    try {
      const response = await apiClient.get(`/orders/${orderId}/receipt`, {
        responseType: 'text'
      });
      
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.contentWindow.document.write(`<pre style="font-family: monospace; font-size: 12px;">${response.data}</pre>`);
      iframe.contentWindow.document.close();
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
      
      toast.success('Adisyon tekrar yazdırılıyor');
    } catch (error) {
      toast.error('Adisyon yazdırılamadı');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Geçmiş Siparişler
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Kapanmış ve ödemesi alınmış adisyonları görüntüleyin</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {orders.map((order) => {
            const date = new Date(order.updated_at || order.created_at);
            return (
              <Card
                key={order.id}
                className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-6 opacity-80 hover:opacity-100 transition-opacity"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Masa {order.table_number}</h3>
                    <div className="flex items-center text-slate-600 dark:text-slate-400 text-sm mt-1 gap-3">
                      <span className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {date.toLocaleDateString('tr-TR')}
                      </span>
                      <span className="flex items-center">
                        <Clock size={14} className="mr-1" />
                        {date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-500/30 border">
                    Tamamlandı
                  </Badge>
                </div>

                <div className="space-y-2 mb-4">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-700 dark:text-slate-300">
                        {item.quantity}x {item.menu_item_name}
                      </span>
                      <span className="text-slate-600 dark:text-slate-400 font-mono">{(item.price * item.quantity).toFixed(2)} ₺</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-slate-200 dark:border-slate-800">
                  <div className="text-slate-600 dark:text-slate-400">Toplam</div>
                  <div className="text-xl font-bold text-slate-900 dark:text-slate-100 font-mono">
                    {order.total_amount.toFixed(2)} ₺
                  </div>
                </div>

                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:bg-slate-700 hover:text-slate-900 dark:text-white">
                          <Info size={16} className="mr-2 text-blue-400" />
                          Ödeme Detayı
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                        <DialogHeader>
                          <DialogTitle>Ödeme Kırılımı (Tüm Masa)</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2 mt-4">
                          {order.checkout_payments && order.checkout_payments.length > 0 ? (
                            order.checkout_payments.map((p, i) => (
                              <div key={i} className="flex justify-between items-center p-3 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-700">
                                <span className={p.method === 'Nakit' ? 'text-emerald-400 font-medium' : 'text-blue-400 font-medium'}>
                                  {p.method}
                                </span>
                                <span className="text-slate-900 dark:text-slate-100 font-mono text-lg">{p.amount.toFixed(2)} ₺</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-slate-600 dark:text-slate-400 text-sm text-center py-4">Bu sipariş eski sistemle kapatıldığı için ödeme detayı bulunmamaktadır.</p>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                {hasPermission('print_past_receipt') && (
                  <div className="mt-4 flex gap-2">
                    <Button
                      onClick={() => printReceipt(order.id)}
                      variant="outline"
                      className="w-full bg-transparent border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:bg-slate-800"
                    >
                      <Printer size={16} className="mr-2" />
                      Tekrar Yazdır
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {orders.length === 0 && (
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-12 text-center">
            <p className="text-slate-600 dark:text-slate-400">Henüz geçmiş sipariş bulunamadı</p>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default PastOrdersPage;
